import React, { useEffect, useRef, useState } from 'react';
import { BookIcon, NewsIcon, TargetIcon, MessageCircleIcon, ChartIcon, BriefcaseIcon, UsersIcon, HelpCircleIcon, UserIcon, BellIcon, DownloadIcon } from './Icons';
import styles from '../styles/HamburgerMenu.module.css';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * Hamburger Menu Component
 * Displays a right-side slide-in panel with navigation options
 *
 * Props:
 * - isOpen: Whether the menu is open
 * - onClose: Callback when menu should close
 * - onNavigateTasks: Navigate to tasks view
 * - onNavigateProgress: Navigate to progress dashboard view
 * - onNavigateArticles: Navigate to all articles view
 * - onNavigateNews: Navigate to all news view
 * - onNavigateCoachDashboard: Navigate to coach dashboard (for coaches)
 * - onNavigateOrgDashboard: Navigate to organization dashboard (for org admins)
 * - onNavigateDevDashboard: Navigate to dev dashboard (development only)
 * - onFeedback: Open feedback modal
 * - onStartTour: Start the onboarding tour
 * - onNavigateSettings: Navigate to settings/profile screen
 * - user: User object with firstname, lastname, email, isCoach, adminOfOrganizations
 */
function HamburgerMenu({
  isOpen,
  onClose,
  onNavigateTasks,
  onNavigateProgress,
  onNavigateArticles,
  onNavigateNews,
  onNavigateCoachDashboard,
  onNavigateOrgDashboard,
  onNavigateDevDashboard,
  onFeedback,
  onStartTour,
  onNavigateSettings,
  user
}) {
  const panelRef = useRef(null);
  const { isInstallable, promptInstall, isIOS } = usePWAInstall();
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Close on escape key
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Update notification permission when menu opens
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle notification permission request
  const handleEnableNotifications = async () => {
    console.log('[HamburgerMenu] Enable notifications clicked, current permission:', notificationPermission);

    if ('Notification' in window) {
      try {
        if (notificationPermission === 'granted') {
          // Show info that they need to disable in device settings
          console.log('[HamburgerMenu] Notifications already granted, showing alert');
          alert('To disable notifications, please go to your device/browser settings and turn off notifications for this app.');
        } else if (notificationPermission === 'denied') {
          // Permission was previously denied - show instructions to reset
          console.log('[HamburgerMenu] Notifications were previously blocked');
          alert('Notifications were blocked. To enable them:\n\n1. Click the lock icon (🔒) or info icon (ⓘ) in your browser\'s address bar\n2. Find "Notifications" in the permissions list\n3. Change it from "Block" to "Allow"\n4. Refresh the page');
        } else {
          // Request permission (permission is 'default')
          console.log('[HamburgerMenu] Requesting notification permission...');
          const permission = await Notification.requestPermission();
          console.log('[HamburgerMenu] Permission result:', permission);
          setNotificationPermission(permission);

          // If still denied after request, show the instructions
          if (permission === 'denied') {
            alert('Notifications were blocked. To enable them:\n\n1. Click the lock icon (🔒) or info icon (ⓘ) in your browser\'s address bar\n2. Find "Notifications" in the permissions list\n3. Change it from "Block" to "Allow"\n4. Refresh the page');
          }
        }
      } catch (error) {
        console.error('[HamburgerMenu] Error requesting notification permission:', error);
      }
    } else {
      console.log('[HamburgerMenu] Notifications not supported in this browser');
    }
  };

  // Handle PWA install
  const handleInstallPWA = async () => {
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else if (isInstallable) {
      await promptInstall();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className={styles.menuOverlay} onClick={() => setShowIOSInstructions(false)}>
          <div className={styles.iosModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.iosModalTitle}>Install Sophia on iOS</div>
            <div className={styles.iosInstructions}>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>1</div>
                <div className={styles.iosStepText}>
                  Tap the <strong>Share</strong> button <span className={styles.iosIcon}>⎘</span> at the bottom of Safari
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>2</div>
                <div className={styles.iosStepText}>
                  Scroll down and tap <strong>"Add to Home Screen"</strong> <span className={styles.iosIcon}>➕</span>
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>3</div>
                <div className={styles.iosStepText}>
                  Tap <strong>"Add"</strong> in the top right corner
                </div>
              </div>
            </div>
            <div className={styles.iosModalButtons}>
              <button
                className={styles.iosModalBtn}
                onClick={() => setShowIOSInstructions(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.menuOverlay} onClick={onClose}>
        <div
          ref={panelRef}
          className={styles.menuPanel}
          onClick={(e) => e.stopPropagation()}
        >
        <div className={styles.menuHeader}>
          <div>
            <h2 className={styles.menuTitle}>Menu</h2>
            {user && (
              <div className={styles.userInfo}>
                <div className={styles.userName}>
                  {user.firstName} {user.lastName}
                </div>
                <div className={styles.userEmail}>{user.email}</div>
              </div>
            )}
          </div>
          <button onClick={onClose} className={styles.closeButton} aria-label="Close menu">
            ×
          </button>
        </div>

        <nav className={styles.menuNav}>
          <button onClick={onNavigateTasks} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <TargetIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Action Plan</span>
          </button>

          <button onClick={onNavigateProgress} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <ChartIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Progress Dashboard</span>
          </button>

          <button onClick={onNavigateArticles} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <BookIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Learning Resources</span>
          </button>

          <button onClick={onNavigateNews} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <NewsIcon size={24} />
            </span>
            <span className={styles.menuLabel}>All News</span>
          </button>

          {/* Coach Dashboard - only shown if user is a coach */}
          {user?.isCoach && onNavigateCoachDashboard && (
            <button onClick={onNavigateCoachDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon}>
                <BriefcaseIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Coach Dashboard</span>
            </button>
          )}

          {/* Organization Dashboard - only shown if user is org admin */}
          {user?.adminOfOrganizations && user.adminOfOrganizations.length > 0 && onNavigateOrgDashboard && (
            <button onClick={onNavigateOrgDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon}>
                <UsersIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Organization Dashboard</span>
            </button>
          )}

          {/* Dev Dashboard - only shown in development mode */}
          {process.env.NODE_ENV === 'development' && onNavigateDevDashboard && (
            <button onClick={onNavigateDevDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon}>
                🛠️
              </span>
              <span className={styles.menuLabel}>Dev Dashboard</span>
            </button>
          )}

          <div className={styles.menuDivider}></div>

          <button onClick={onFeedback} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <MessageCircleIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Send Feedback</span>
          </button>

          {/* Notification Settings */}
          {'Notification' in window && (
            <button
              onClick={handleEnableNotifications}
              className={styles.menuItem}
              data-tour="enable-notifications"
            >
              <span className={styles.menuIcon}>
                <BellIcon size={24} />
              </span>
              <span className={styles.menuLabel}>
                {notificationPermission === 'granted' ? (
                  'Notifications Enabled ✓'
                ) : notificationPermission === 'denied' ? (
                  <>
                    Notifications Blocked
                    <span className={styles.menuLabelHelper}>(Tap for Help)</span>
                  </>
                ) : (
                  'Enable Notifications'
                )}
              </span>
            </button>
          )}

          {/* PWA Install */}
          {(isInstallable || isIOS) && (
            <button
              onClick={handleInstallPWA}
              className={styles.menuItem}
              data-tour="install-app"
            >
              <span className={styles.menuIcon}>
                <DownloadIcon size={24} />
              </span>
              <span className={styles.menuLabel}>
                Install App
              </span>
            </button>
          )}

          <button onClick={onStartTour} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <HelpCircleIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Tutorial</span>
          </button>

          <div className={styles.menuDivider}></div>

          <button onClick={onNavigateSettings} className={styles.menuItem}>
            <span className={styles.menuIcon}>
              <UserIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Settings</span>
          </button>
        </nav>
      </div>
    </div>
    </>
  );
}

export default HamburgerMenu;
